import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { css, styled } from 'styled-components';
import { tk, verticalGap } from '@sunwebgroup/ui-components';

import { ItemContextProvider } from '@/scopes/search/components/QuickSearchBar/Item/context';
import { hasActiveItemSelector, useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { QuickSearchDrawer } from '@/scopes/search/components/QuickSearchBar/QuickSearchContent';

export default function QuickSearchBarResponsive({
  disabled,
  children,
  className,
}: PropsWithChildren<{ disabled?: boolean; className?: string }>) {
  const hasActiveItem = useQuickSearchStore(hasActiveItemSelector);
  const { isDesktopMd, isMobile } = useBreakpoints();
  let itemVariant: 'popover' | 'dialog' | 'icon' | 'sheet';
  if (isDesktopMd) {
    itemVariant = 'popover';
  } else if (isMobile) {
    itemVariant = 'sheet';
  } else {
    itemVariant = 'dialog';
  }
  return (
    <Wrapper className={className}>
      <Drawer $hasActiveItem={hasActiveItem} $isDisabled={disabled}>
        <InputItems>
          <ItemContextProvider value={{ type: itemVariant, responsive: true }}>{children}</ItemContextProvider>
        </InputItems>
      </Drawer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  padding: 0 ${tk('spacing_md')};
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Drawer = styled(QuickSearchDrawer)<{ $hasActiveItem: boolean; $isDisabled?: boolean }>`
  width: calc(100% - 32px);
  max-width: 480px;
  margin: 0 auto;
  left: 0;
  right: 0;
  position: absolute;
  top: 203px;
  @media ${tk('mq_tablet')} {
    top: 229px;
  }
  @media ${tk('mq_desktop_md')} {
    top: 277px;
    display: flex;
    max-width: 960px;
    padding: 8px 16px 8px 8px;
    min-height: 80px;
    border-radius: 40px;
    ${({ $hasActiveItem }) => $hasActiveItem && hasActiveItemMod()}
  }
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      background-color: ${tk('color_semantic_bg_disabled')};
    `}
`;

const hasActiveItemMod = () => css`
  background-color: ${tk('color_semantic_bg_subdued_hover')};
`;

const InputItems = styled.div`
  ${verticalGap('spacing_xs')};
  @media ${tk('mq_desktop_md')} {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: unset;

    .input-item,
    .sheet-item {
      flex: 1 0 0;
      min-width: initial;
      height: 100%;
      justify-content: center;
      padding: 0 6px 0 12px;

      &:first-child {
        box-sizing: border-box;
        padding-left: 20px;
      }

      &:nth-last-child(2) {
        margin-right: 8px;
      }

      > p {
        font-size: 15px;
        line-height: 1.5;
        letter-spacing: 0.3px;
      }
    }

    .pill-item {
      height: 100%;
      flex: 1 0 0;
      min-width: initial;

      button {
        &:before {
          width: 0;
        }
        &:hover {
          background-color: #e7e0d7;
        }
        &:disabled:hover {
          background-color: ${tk('color_semantic_bg_disabled')};
        }
      }

      &:first-child {
        button {
          padding-left: 20px;
          border-radius: 50px ${tk('radius_medium')} ${tk('radius_medium')} 50px;
        }
      }

      &:nth-last-child(2) {
        margin-right: 8px;
        button {
          border-radius: ${tk('radius_medium')} 50px 50px ${tk('radius_medium')};
        }
      }

      > * {
        padding: 0 6px 0 12px;
        border-radius: 4px;
      }
    }

    > * {
      margin-bottom: unset;
      border-width: 0;
      border-radius: ${tk('radius_rounded')};
      position: relative;
      font-size: 14px;
      width: 100%;

      &:first-child {
        &:after {
          display: none;
        }
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        margin: auto 0;
        height: 40px;
        width: 1px;
        background: #ded2c4;
      }

      &:last-child {
        width: 235px;

        &:after {
          display: none;
        }
      }
    }
  }
`;
